import { useEffect, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { TextField, Dropdown, Button, Typography, InlineAlert } from '@nwh-consumer-digital/nw-component-lib'
import { Grid, Box, Paper } from '@mui/material'
import styled from '@emotion/styled'
import { useProgress } from '../context/ProgressContext'

const Item = styled(Paper)(() => ({
  textAlign: 'left',
  maxWidth: '400px',
  marginBottom: '18px', // There is an extra 6 pixels this makes 24px
}))

const minorPageOne = (props) => {
  const { SubmissionState, onFormStateChange } = props
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({ mode: 'onBlur' })
  //UseProgress Context Hook
  const { updateProgress } = useProgress()

  useEffect(() => {
    // Set Value For Progress
    updateProgress(30)

    if (SubmissionState.sectionOneForm) {
      setValue('firstName', SubmissionState.sectionOneForm?.firstName)
      setValue('lastName', SubmissionState.sectionOneForm?.lastName)
      setValue('mInitial', SubmissionState.sectionOneForm?.mInitial)
      setValue('DOBChild', SubmissionState.sectionOneForm?.DOBChild)
      setValue('gender', SubmissionState.sectionOneForm?.gender)
      setValue('streetNumber', SubmissionState.sectionOneForm?.streetNumber)
      setValue('City', SubmissionState.sectionOneForm?.City)
      setValue('State', SubmissionState.sectionOneForm?.State)
      setValue('Zip', SubmissionState.sectionOneForm?.Zip)
      setValue('relationshiptoPatient', SubmissionState.sectionOneForm?.relationshiptoPatient)
    }
  }, [])

  // Count the number of errors on the page and, if any are found, scroll to the reference above the inline alert box after tallying them.
  const errorCount = Object.keys(errors).length
  const targetRef = useRef(null)
  const scrollToTarget = () => {
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }
  useEffect(() => {
    if (errorCount > 0) {
      scrollToTarget()
    }
  }, [errors])

  const handleRegistration = (data) => {
    SubmissionState.sectionOneForm = data
    SubmissionState.goBackPageTwo = true
    onFormStateChange()
  }

  return (
    <Box>
      <Grid container direction="column" justifyContent="space-between" alignItems="left">
        <Grid item xs={12}>
          <Typography
            variant="h1"
            sx={{
              marginTop: { xs: '1.25rem', sm: '1.25rem', md: '1.25rem', lg: '5rem', xl: '5rem' },
              marginBottom: { xs: '2rem', sm: '2rem', md: '2rem', lg: '2rem', xl: '2rem' },
            }}>
            Minor’s information
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            maxWidth: window.innerWidth <= 768 ? '400px' : 'none',
            marginBottom: 24,
            textAlign: 'justify',
            lineHeight: 1.6,
            wordSpacing: '0.05em',
          }}>
          <Typography type="body" size="large" weight="light">
            {' '}
            If you are the parent or legal guardian of a Northwell Health patient who is under the age of 13, you can
            complete this form to request “proxy access” to your child’s health information in Northwell patient portals
            (MyNorthwell, FollowMyHealth). Proxy access enables you to view some of your child’s health information and,
            in some instances, communicate through the portal with your child’s health care team. When your child turns
            13, you will no longer be able to view any new information in Northwell patient portals.
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            maxWidth: window.innerWidth <= 768 ? '400px' : 'none',
            marginBottom: 24,
            textAlign: 'justify',
            lineHeight: 1.6,
            wordSpacing: '0.05em',
          }}>
          <Typography type="body" size="large" weight="light">
            {' '}
            Proxy Access does not affect your right to request access to your child’s medical records through other
            means. Please note, billing information is not automatically available to proxies. Only the patient and the
            individual(s) who is financially responsible for paying the patient's medical bills (guarantor) may see
            billing information. Written requests for copies of medical records may be submitted to Northwell’s Health
            Information Management Department. Please contact the provider’s front office staff with any questions or
            concerns. 
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            maxWidth: window.innerWidth <= 768 ? '400px' : 'none',
            marginBottom: 24,
            textAlign: 'justify',
            lineHeight: 1.6,
            wordSpacing: '0.05em',
          }}>
          <Typography type="body" size="large" weight="light">
            {' '}
            Enter the minor's information details below whom you are requesting proxy access for.
          </Typography>
        </Grid>
        <form onSubmit={handleSubmit(handleRegistration)}>
          <Grid item xs={12} className="requiredSpacing">
            <Item elevation={0}>
              <label ref={targetRef} className="required">
                <span className="red">*</span> Required
              </label>
            </Item>
          </Grid>
          {errorCount > 0 && (
            <Grid item xs={12}>
              <Item elevation={0}>
                <InlineAlert severity="error" fullWidth text={`There are ${errorCount} errors on this page.`} />
              </Item>
            </Grid>
          )}
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="firstName"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="firstName"
                    onChange={onChange}
                    defaultValue={SubmissionState.sectionOneForm?.firstName}
                    label={
                      <span>
                        First Name <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.firstName)}
                    helperText={
                      errors?.firstName
                        ? errors?.firstName?.type === 'required'
                          ? 'First name is required.'
                          : errors?.firstName?.type === 'pattern'
                          ? 'First name contains invalid characters.'
                          : 'First name is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="lastName"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="lastName"
                    defaultValue={SubmissionState.sectionOneForm?.lastName}
                    onChange={onChange}
                    label={
                      <span>
                        Last Name <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.lastName)}
                    helperText={
                      errors?.lastName
                        ? errors?.lastName?.type === 'required'
                          ? 'Last name is required.'
                          : errors?.lastName?.type === 'pattern'
                          ? 'Last name contains invalid characters.'
                          : 'Last name is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="mInitial"
                control={control}
                rules={{ pattern: { value: /^[A-Za-z]+$/, message: 'Please use alphabetic characters only' } }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    id="mInitial"
                    onChange={onChange}
                    value={SubmissionState.sectionOneForm?.mInitial}
                    label="Middle initial"
                    fullWidth
                    error={Boolean(errors?.mInitial)}
                    helperText={errors?.mInitial?.message || ''}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="DOBChild"
                control={control}
                rules={{
                  pattern:
                    /^(0[1-9]|1[0-2])\/(0[1-9]|[1-2][0-9]|3[0-1])\/(19\d{2}|20[0-2]\d|20[2-9][0-9]|20[3-9][0-9]|21\d{2})$/,
                  required: true,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="DOBChild"
                    defaultValue={SubmissionState.sectionOneForm?.DOBChild}
                    onChange={onChange}
                    label={
                      <span>
                        Date of birth (MM/DD/YYYY) <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.DOBChild)}
                    helperText={
                      errors?.DOBChild
                        ? errors?.DOBChild?.type === 'required'
                          ? 'Date of Birth is required.'
                          : errors?.DOBChild?.type === 'pattern'
                          ? 'Please use numeric characters only, and correct Month/Date/Year values.'
                          : 'Date of Birth is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="gender"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <Dropdown
                    id="gender"
                    label={
                      <span>
                        Birth gender <span className="red">*</span>
                      </span>
                    }
                    value={value}
                    onChange={onChange}
                    error={Boolean(errors?.gender)}
                    helperText={errors?.gender ? 'Birth gender is required' : undefined}
                    selectOptions={[
                      { name: 'Female', value: 'Female' },
                      { name: 'Male', value: 'Male' },
                      { name: 'Other', value: 'Other' },
                    ]}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="streetNumber"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Za-z0-9 -]+$/,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="streetNumber"
                    defaultValue={SubmissionState.sectionOneForm?.streetNumber}
                    onChange={onChange}
                    label={
                      <span>
                        Address <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.streetNumber)}
                    helperText={
                      errors?.streetNumber
                        ? errors?.streetNumber?.type === 'required'
                          ? 'Address is required.'
                          : errors?.streetNumber?.type === 'pattern'
                          ? 'Please use alphabetic characters only'
                          : 'Address is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="City"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="City"
                    defaultValue={SubmissionState.sectionOneForm?.City}
                    onChange={onChange}
                    label={
                      <span>
                        City <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.City)}
                    helperText={
                      errors?.City
                        ? errors?.City?.type === 'required'
                          ? 'City is required.'
                          : errors?.City?.type === 'pattern'
                          ? 'Please use alphabetic characters only'
                          : 'City is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="State"
                control={control}
                rules={{
                  required: true,
                  pattern: /^[A-Z a-z]*$/i,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="State"
                    defaultValue={SubmissionState.sectionOneForm?.State}
                    onChange={onChange}
                    label={
                      <span>
                        State <span className="red">*</span>
                      </span>
                    }
                    fullWidth
                    error={Boolean(errors?.State)}
                    helperText={
                      errors?.State
                        ? errors?.State?.type === 'required'
                          ? 'State is required.'
                          : errors?.State?.type === 'pattern'
                          ? 'Please use alphabetic characters only'
                          : 'State is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="Zip"
                control={control}
                rules={{
                  required: true,
                  pattern: /(^\d{5}$)|(^\d{5}-\d{4}$)/,
                }}
                render={({ field: { onChange } }) => (
                  <TextField
                    id="Zip"
                    onChange={onChange}
                    defaultValue={SubmissionState.sectionOneForm?.Zip}
                    label={
                      <span>
                        Zip <span className="red">*</span>
                      </span>
                    }
                    error={errors?.Zip}
                    fullWidth
                    helperText={
                      errors?.Zip
                        ? errors?.Zip?.type === 'required'
                          ? 'Zipcode is required.'
                          : errors?.Zip?.type === 'pattern'
                          ? 'Please use 5 digits of numeric characters only'
                          : 'Zipcode is required.'
                        : ''
                    }
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid item xs={12}>
            <Item elevation={0}>
              <Controller
                name="relationshiptoPatient"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange } }) => (
                  <Dropdown
                    id="relationshiptoPatient"
                    defaultValue={SubmissionState.sectionOneForm?.relationshiptoPatient || []}
                    label={
                      <span>
                        Relationship to patient <span className="red">*</span>
                      </span>
                    }
                    selectOptions={[
                      { name: 'Parent', value: 'Parent' },
                      { name: 'Legal guardian', value: 'Legal guardian' },
                    ]}
                    onChange={onChange}
                    error={Boolean(errors?.relationshiptoPatient)}
                    helperText={errors?.relationshiptoPatient ? 'Relationship to patient is required' : undefined}
                  />
                )}
              />
            </Item>
          </Grid>
          <Grid>
            <Item elevation={0}>
              <Button text="Continue" color="primary" type="submit" />
            </Item>
          </Grid>
        </form>
      </Grid>
    </Box>
  )
}
export default minorPageOne
